import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import { useAccount } from '@/frontastic';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { getCurrentDateTime, getAgentName, TranslateText } from '@/helpers/utils/siteUtils';
import OrderItemsHeader from './common/orderItemsHeader';
import OrderItem from './orderLineItems';
import { DEFAULT_SITE_LANG, GROUP_ORDER_TITLE, RETURN_LABEL_SENT } from '../../../../../../constants/constant';
import { PAYLOAD_QUERY, RESPONSE_SUCCESS_MESSAGE } from '../../../customer-detail/constants';
import AdjustItemModal from '../../../modals/orderDetails/adjustItem';
import AdvanceShippingOptionsModal from '../../../modals/orderDetails/advanceShippingOptions';
import SendTracerModal from '../../../modals/orderDetails/sendTracer';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import Loader from '../../../ui/loader';
import { useToast } from '../../../ui/use-toast';
import { PageTexts } from '../../i18n';
import { InsuranceInfo, Item, LineItems, OrderStatus, TrackingInfo } from '../../types';
const OrderItems = ({
  RecipientAddresses,
  lineItems,
  customerId,
  addNewNotes,
  RecipientLineItems,
  canSendTracer,
  getUpdatedData,
  GroupedItems,
  showAdvanceShippingOption,
  isSignatureRequired,
  data,
  canSendReturnLabel,
  emailAddress,
  showActiveBrandSection = true,
  insuranceInfo,
  firstName,
}: {
  RecipientAddresses: any;
  lineItems: LineItems;
  customerId: string;
  addNewNotes: any;
  RecipientLineItems: any;
  canSendTracer: boolean;
  getUpdatedData: () => void;
  GroupedItems: OrderStatus[];
  showAdvanceShippingOption: boolean;
  isSignatureRequired?: boolean;
  canSendReturnLabel: boolean;
  data: any;
  emailAddress: string;
  showActiveBrandSection?: boolean;
  insuranceInfo?: InsuranceInfo;
  firstName: string;
}) => {
  const { toast } = useToast();
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const [open, setOpen] = useState(false);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [openTracerModal, setOpenTracerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { addOrderNote, getOrderEvents, sendClaimInsuranceEmail } = useAccount();
  const [agentName] = useState(getAgentName().replace(/@.*/, ''));
  const isThirdPartyPayment = data?.data?.dataSource?.Order?.Payments?.[0]?.PaymentInfo?.isThirdPartyPayment;
  const showCancelLink = insuranceInfo?.isCancelable ?? false;
  const showClaimLink = insuranceInfo?.canShowClaimLink ?? false;
  const params = useSearchParams();
  let ShipmentId = 0;
  const AdjustedItems = RecipientLineItems?.filter(
    (item: any) => item?.PostSubmissionInfo?.AvailableActions?.isPriceAdjustable,
  );

  const calculatedTotalItemsCount = useMemo(() => {
    return GroupedItems?.flatMap((group: any) => group?.shipmentInfos).reduce((totalCount: any, shipmentInfo: any) => {
      return totalCount + shipmentInfo?.items?.length;
    }, 0);
  }, [GroupedItems]);
  const sendReturnLink = data?.sendReturnLink ?? '';

  useEffect(() => {
    setTotalItemsCount(calculatedTotalItemsCount);
  }, [calculatedTotalItemsCount]);

  const AddNewNote = async (note: string) => {
    const currentDate = getCurrentDateTime();
    const payload = {
      data: {
        Note: {
          createdBy: agentName,
          createdDate: currentDate,
          isPriority: true,
          text: note,
        },
      },
      query: {
        orderId: params.get(PAYLOAD_QUERY.ORDER_ID),
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
      },
    };
    await addOrderNote(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        setOpen(false);
        addNewNotes();
      }
    });
  };

  const handleCancelShippingInsurance = async () => {
    setLoading(true);
    const payload = {
      query: {
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        orderId: params.get(PAYLOAD_QUERY.ORDER_ID),
      },
      data: {
        eventType: mapLocaleToMeaningfulFormat().eventRequestCancelLines,
        createdBy: agentName,
        OrderLines: [
          {
            lineId: insuranceInfo?.lineId?.toString() ?? '1',
            message: mapLocaleToMeaningfulFormat().messageChangedMind,
          },
        ],
      },
    } as any;
    await getOrderEvents(payload)
      .then((response) => {
        if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
          if (getUpdatedData) {
            getUpdatedData?.();
          }
          SuccessNotification(toast, {
            description: 'Worry-Free Delivery® was canceled successfully.',
            action: <SuccessIcon className="h-5 w-5" />,
          });
        } else {
          ErrorNotification(toast, {
            description: 'Error while cancelling Worry-Free Delivery®.',
            action: <ErrorIcon className="h-5 w-5" />,
          });
        }
      })
      .catch((error) => console.error(error));
    setLoading(false);
  };

  const handleFileAClaim = async () => {
    setLoading(true);
    const payload = {
      email: insuranceInfo?.email,
      orderNumber: insuranceInfo?.orderId,
      phoneNumber: insuranceInfo?.phoneNumber?.replace(/-/g, ''),
      firstName: firstName,
    };
    await sendClaimInsuranceEmail(payload)
      .then((response) => {
        if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
          SuccessNotification(toast, {
            description: 'Claim Insurance email Sent successfully.',
            action: <SuccessIcon className="h-5 w-5" />,
          });
        } else {
          ErrorNotification(toast, {
            description: 'Error while sending Claim Insurance email.',
            action: <ErrorIcon className="h-5 w-5" />,
          });
        }
      })
      .catch((error) => console.error(error));
    setLoading(false);
  };

  return (
    <>
      {loading && <Loader />}
      <Card className="w-full rounded-md border-foggyGray shadow-lg">
        <CardHeader className="p-5">
          <CardTitle className="flex flex-wrap">
            <p className="mr-auto text-lg font-bold">
              {totalItemsCount} {Texts.itemsInOrder}
            </p>
            <section className="flex flex-wrap gap-2">
              {showCancelLink && (
                <button
                  className="rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
                  onClick={handleCancelShippingInsurance}
                >
                  {mapLocaleToMeaningfulFormat().cancelWorryFreeDelivery}
                </button>
              )}
              {showClaimLink && (
                <button
                  className="rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
                  onClick={handleFileAClaim}
                >
                  {mapLocaleToMeaningfulFormat().sendClaimEmail}
                </button>
              )}
              {showActiveBrandSection && (
                <AdvanceShippingOptionsModal
                  Texts={Texts}
                  showAdvanceShippingOption={showAdvanceShippingOption}
                  isSignatureRequired={isSignatureRequired}
                ></AdvanceShippingOptionsModal>
              )}

              {AdjustedItems?.length > 0 && showActiveBrandSection ? (
                <button
                  className="rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
                  onClick={() => setOpen(true)}
                >
                  {Texts.adjustItems}
                </button>
              ) : (
                <></>
              )}
              {open && (
                <AdjustItemModal
                  open={open}
                  setOpen={setOpen}
                  AdjustedItems={AdjustedItems}
                  lineItems={lineItems?.lineItems}
                  Texts={Texts}
                  getUpdatedData={getUpdatedData}
                ></AdjustItemModal>
              )}
              {canSendTracer && showActiveBrandSection ? (
                <button
                  className="rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
                  onClick={() => setOpenTracerModal(true)}
                >
                  {Texts.sendTracer}
                </button>
              ) : (
                <></>
              )}
              {openTracerModal && (
                <SendTracerModal
                  open={openTracerModal}
                  setOpen={setOpenTracerModal}
                  Texts={Texts}
                  orderItems={lineItems}
                  RecipientAddresses={RecipientAddresses}
                  customerId={customerId}
                  addNewNotes={addNewNotes}
                  isThirdPartyPayment={isThirdPartyPayment}
                  emailAddress={emailAddress}
                />
              )}
              {canSendReturnLabel && showActiveBrandSection ? (
                <Link
                  href={sendReturnLink}
                  className=" flex h-[30px] items-center justify-center rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
                  target="_blank"
                  onClick={() => AddNewNote(RETURN_LABEL_SENT)}
                >
                  {Texts.sendReturnLabelText}
                </Link>
              ) : (
                <></>
              )}
            </section>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {GroupedItems?.map((groupItems: OrderStatus) => (
            <section key={groupItems?.title}>
              {groupItems?.shipmentInfos?.map((groupItem: any, index: number) => {
                if (groupItems?.title === GROUP_ORDER_TITLE.SHIPPED) {
                  ShipmentId += 1;
                }
                return (
                  <section key={`Item-${groupItems?.title}-${index}`}>
                    {groupItems?.title !== GROUP_ORDER_TITLE.AWAITING_SHIPMENT ? (
                      <>
                        {groupItems?.title === GROUP_ORDER_TITLE.SHIPPED && (
                          <h4 className="mb-1 font-bold">
                            {Texts.shipmentNo}
                            {ShipmentId}
                          </h4>
                        )}
                        <section className="flex-col-2 mb-5 flex items-baseline gap-4">
                          <p>
                            <span className={`text-[${groupItem?.shipmentStatus?.colorCode}] text-lg font-bold`}>
                              {groupItem?.shipmentStatus?.status}
                            </span>{' '}
                          </p>
                          <section className="flex flex-col">
                            {groupItem?.trackingInfos?.length > 0 &&
                              groupItem?.trackingInfos?.map((trackingDetail: TrackingInfo) => {
                                const trackingLink = trackingDetail?.trackingLink ?? '';
                                const trackingCode = trackingDetail?.trackingNumber ?? '';
                                const externalTrackingLink = trackingDetail?.externalTrackingLink;
                                return (
                                  <section key={trackingCode}>
                                    <button
                                      className="h-[39px] w-[151px] rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-[15px] font-medium"
                                      onClick={() => {
                                        const trackingUrl = externalTrackingLink || trackingLink;

                                        if (trackingUrl) {
                                          window.open(trackingUrl, '_blank', 'noopener,noreferrer');
                                        }
                                      }}
                                    >
                                      {Texts?.trackShipmentTitle}
                                    </button>
                                  </section>
                                );
                              })}
                          </section>
                        </section>
                        <OrderItemsHeader Texts={Texts} />
                      </>
                    ) : index === 0 ? (
                      <OrderItemsHeader Texts={Texts} />
                    ) : (
                      <></>
                    )}
                    {groupItem?.items?.map((LineItem: Item) => {
                      return (
                        <section key={LineItem?.lineSequence}>
                          <OrderItem Texts={Texts} item={LineItem} getUpdatedData={getUpdatedData} />
                        </section>
                      );
                    })}
                  </section>
                );
              })}
            </section>
          ))}
        </CardContent>
      </Card>
    </>
  );
};

export default OrderItems;
